/**
 * EmbroideryThreadModal
 *
 * Where the seller decides which thread each design color is stitched in. The
 * motif carries more colors than the machine has needles, so the real work is
 * folding several design colors onto one thread until the count fits.
 *
 * The stitch preview renders locally on every change: a round trip per dropdown
 * would make the one interaction this dialog exists for feel broken.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle, Trash2, Check } from 'lucide-react';
import {
  THREAD_COLORS,
  countThreads,
  threadById,
  type ThreadColor,
} from '@designdeck/shared';
import { useEmbroideryStore } from '@/stores/embroideryStore';
import {
  renderEmbroideryPreview,
  PREVIEW_EDGE,
} from '@/lib/embroidery/renderPreview';
import { cn } from '@/lib/utils';
import { loadImage } from '@/utils/mockupLayers';
import { logger } from '@/utils/logger';

interface EmbroideryThreadModalProps {
  isOpen: boolean;
  /** Blob URL or storage URL the render reads its pixels from. */
  sourceUrl: string | null;
  onCancel: () => void;
  onConfirm: (mapping: Record<string, string>) => Promise<void>;
  onRemoveMotif: () => void;
}

type FabricTone = 'light' | 'dark';

function ThreadSwatch({ hex, size }: { hex: string; size: number }) {
  return (
    <span
      aria-hidden="true"
      className="flex-none rounded-full ring-1 ring-black/15"
      style={{ width: size, height: size, background: hex }}
    />
  );
}

/**
 * Thread picker. A native <select> cannot show a color swatch next to each
 * option, so this is a listbox — which means the keyboard contract is ours to
 * implement rather than the browser's.
 */
function ThreadSelect({
  value,
  onChange,
  isOpen,
  onOpenChange,
  labelledBy,
}: {
  value: string;
  onChange: (threadId: string) => void;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  labelledBy: string;
}) {
  const { t } = useTranslation();
  const listRef = useRef<HTMLDivElement>(null);
  const [activeIndex, setActiveIndex] = useState(() =>
    Math.max(
      0,
      THREAD_COLORS.findIndex((t) => t.id === value)
    )
  );

  const selected: ThreadColor | undefined = threadById(value);
  const activeThread: ThreadColor | undefined = THREAD_COLORS[activeIndex];
  const triggerId = `${labelledBy}-thread`;

  useEffect(() => {
    if (isOpen) {
      setActiveIndex(
        Math.max(
          0,
          THREAD_COLORS.findIndex((t) => t.id === value)
        )
      );
      listRef.current?.focus();
    }
  }, [isOpen, value]);

  const commit = (index: number) => {
    const picked = THREAD_COLORS[index];
    if (!picked) return;
    onChange(picked.id);
    onOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      // Without this the modal's window-level Escape handler also fires and
      // closes the whole dialog, discarding the mapping in progress.
      e.stopPropagation();
      e.preventDefault();
      onOpenChange(false);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => {
        const next = e.key === 'ArrowDown' ? i + 1 : i - 1;
        return (next + THREAD_COLORS.length) % THREAD_COLORS.length;
      });
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      commit(activeIndex);
    }
  };

  return (
    <div className="relative flex-1 min-w-0">
      <button
        type="button"
        id={triggerId}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls="thread-listbox"
        // Both halves: which design color this row is for, and which thread is
        // currently on it. Labelling by the design color alone would announce
        // the row without ever naming the value it holds.
        aria-labelledby={`${labelledBy} ${triggerId}`}
        onClick={() => onOpenChange(!isOpen)}
        className="w-full flex items-center gap-2 h-9 px-2.5 rounded-control bg-paper-2 ring-1 ring-line text-sm text-ink hover:bg-paper transition-colors"
      >
        <ThreadSwatch hex={selected?.hex ?? '#000000'} size={16} />
        <span className="flex-1 min-w-0 truncate text-left">
          {selected
            ? t(`threads.${selected.id}`, { defaultValue: selected.name })
            : value}
        </span>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => onOpenChange(false)}
          />
          <div
            id="thread-listbox"
            ref={listRef}
            role="listbox"
            tabIndex={-1}
            aria-activedescendant={
              activeThread ? `thread-option-${activeThread.id}` : undefined
            }
            onKeyDown={handleKeyDown}
            className="absolute right-0 z-20 mt-1 w-60 max-h-60 overflow-y-auto rounded-control bg-paper-2 ring-1 ring-line ring-1 ring-line p-1 focus:outline-none"
          >
            {THREAD_COLORS.map((thread, index) => (
              <div
                key={thread.id}
                id={`thread-option-${thread.id}`}
                role="option"
                aria-selected={thread.id === value}
                onClick={() => commit(index)}
                onMouseEnter={() => setActiveIndex(index)}
                className={cn(
                  'flex items-center gap-2.5 h-9 px-2.5 rounded-chip cursor-pointer text-sm',
                  index === activeIndex ? 'bg-primary-50' : 'bg-transparent',
                  thread.id === value ? 'font-medium' : 'font-normal'
                )}
              >
                <ThreadSwatch hex={thread.hex} size={16} />
                <span className="flex-1 truncate">
                  {t(`threads.${thread.id}`, { defaultValue: thread.name })}
                </span>
                {thread.id === value && (
                  <Check className="h-3.5 w-3.5 text-primary" />
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export function EmbroideryThreadModal({
  isOpen,
  sourceUrl,
  onCancel,
  onConfirm,
  onRemoveMotif,
}: EmbroideryThreadModalProps) {
  const { t } = useTranslation();
  const palette = useEmbroideryStore((s) => s.palette);
  const draftMapping = useEmbroideryStore((s) => s.draftMapping);
  const autoMergedHexes = useEmbroideryStore((s) => s.autoMergedHexes);
  const maxColors = useEmbroideryStore((s) => s.maxColors);
  const assignThread = useEmbroideryStore((s) => s.assignThread);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const confirmButtonRef = useRef<HTMLButtonElement>(null);

  const [tone, setTone] = useState<FabricTone>('light');
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const threadCount = countThreads(draftMapping);
  const isOverLimit = threadCount > maxColors;
  const isEmptyPalette = palette.length === 0;
  const canConfirm = !isOverLimit && !isEmptyPalette && !isBusy;

  const mergedCount = autoMergedHexes.length;

  // Load the source pixels. crossOrigin matters for the storage-hosted case:
  // without it the render canvas is tainted and getImageData throws.
  useEffect(() => {
    if (!isOpen || !sourceUrl) {
      setImage(null);
      return;
    }
    let cancelled = false;
    loadImage(sourceUrl)
      .then((img) => {
        if (!cancelled) setImage(img);
      })
      .catch(() => {
        if (cancelled) return;
        setImage(null);
        logger.warn(
          'EmbroideryThreadModal',
          `Could not load embroidery source: ${sourceUrl}`
        );
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, sourceUrl]);

  // Re-render the stitch preview whenever the mapping changes.
  useEffect(() => {
    const target = canvasRef.current;
    if (!isOpen || !target || !image || palette.length === 0) return;

    const { canvas: rendered } = renderEmbroideryPreview({
      image,
      palette,
      mapping: draftMapping,
      threads: THREAD_COLORS,
      maxEdge: PREVIEW_EDGE,
    });

    target.width = rendered.width;
    target.height = rendered.height;
    const ctx = target.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, target.width, target.height);
    ctx.drawImage(rendered, 0, 0);
  }, [isOpen, image, palette, draftMapping]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isBusy) onCancel();
    },
    [onCancel, isBusy]
  );

  useEffect(() => {
    if (!isOpen) {
      setIsBusy(false);
      setOpenRow(null);
      return;
    }
    window.addEventListener('keydown', handleKeyDown);
    // Focus the primary action — unless it opens disabled, in which case
    // focusing it would leave nothing in the dialog focused at all.
    if (canConfirm) confirmButtonRef.current?.focus();
    else cancelButtonRef.current?.focus();
    return () => window.removeEventListener('keydown', handleKeyDown);
    // Focus is an on-open concern; re-running on every mapping edit would steal
    // focus away from the dropdown the seller is using.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, handleKeyDown]);

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setIsBusy(true);
    try {
      await onConfirm(draftMapping);
    } finally {
      // Left busy-free even on failure: the caller keeps the modal open so the
      // seller can retry rather than losing the mapping.
      setIsBusy(false);
    }
  };

  const rows = useMemo(
    () =>
      palette.map((entry) => ({
        ...entry,
        threadId: draftMapping[entry.hex] ?? entry.suggestedThreadId,
        wasAutoMerged: autoMergedHexes.includes(entry.hex),
      })),
    [palette, draftMapping, autoMergedHexes]
  );

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 z-[100] flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="embroidery-threads-title"
    >
      {/* Busy covers the backdrop and the X as well as the buttons: closing
          mid-apply would restore the draft while the upload still lands. */}
      <div
        className="absolute inset-0 bg-black/45"
        onClick={() => {
          if (!isBusy) onCancel();
        }}
      />

      <motion.div
        initial={{ opacity: 0, scale: 0.97, y: 10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="relative bg-paper-2 rounded-card ring-1 ring-line w-full max-w-4xl max-h-[calc(100vh-3rem)] overflow-y-auto p-6"
      >
        <div className="flex items-start gap-4 mb-5">
          <div className="flex-1 min-w-0">
            <p className="dc-type-label text-mute">
              {t('creator.embroidery.kicker')}
            </p>
            <h3
              id="embroidery-threads-title"
              className="text-lg font-semibold text-ink"
            >
              {t('creator.embroidery.title')}
            </h3>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={isBusy}
            title={t('common.close')}
            aria-label={t('creator.embroidery.closeAria')}
            className="flex-none w-8 h-8 grid place-items-center rounded-control text-mute hover:bg-paper-2 transition-colors disabled:opacity-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {isEmptyPalette && (
          <div className="mb-5 flex items-start gap-2.5 rounded-control border border-dashed border-warning bg-[color-mix(in_srgb,var(--warning)_10%,transparent)] px-4 py-3">
            <AlertTriangle className="h-4 w-4 flex-none text-warning mt-0.5" />
            <p className="text-sm text-ink-2">
              {t('creator.embroidery.emptyPalette')}
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5 items-start">
          {/* Preview */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-3">
              <p className="dc-type-label text-mute">
                {t('creator.embroidery.stitchPreview')}
              </p>
              <div className="flex items-center gap-2">
                <span className="dc-type-label text-mute">
                  {t('creator.embroidery.fabric')}
                </span>
                {(['light', 'dark'] as const).map((option) => (
                  <button
                    key={option}
                    type="button"
                    title={
                      option === 'light'
                        ? t('creator.embroidery.lightFabric')
                        : t('creator.embroidery.darkFabric')
                    }
                    aria-label={
                      option === 'light'
                        ? t('creator.embroidery.previewLight')
                        : t('creator.embroidery.previewDark')
                    }
                    aria-pressed={tone === option}
                    onClick={() => setTone(option)}
                    className={cn(
                      'w-5 h-5 rounded-full transition-shadow',
                      tone === option
                        ? 'ring-2 ring-primary ring-offset-2'
                        : 'ring-1 ring-black/20'
                    )}
                    style={{
                      background: option === 'light' ? '#dcd7cb' : '#26262a',
                    }}
                  />
                ))}
              </div>
            </div>

            <div
              className="relative rounded-control overflow-hidden grid place-items-center min-h-[260px] p-4"
              style={{
                backgroundColor: tone === 'dark' ? '#26262a' : '#dcd7cb',
                backgroundImage:
                  tone === 'dark'
                    ? 'repeating-linear-gradient(0deg, rgba(255,255,255,0.07) 0 1px, transparent 1px 3px), repeating-linear-gradient(90deg, rgba(255,255,255,0.07) 0 1px, transparent 1px 3px)'
                    : 'repeating-linear-gradient(0deg, rgba(0,0,0,0.07) 0 1px, transparent 1px 3px), repeating-linear-gradient(90deg, rgba(0,0,0,0.07) 0 1px, transparent 1px 3px)',
              }}
            >
              <canvas
                ref={canvasRef}
                className="max-w-full max-h-[340px] w-auto h-auto"
              />
              {!image && !isEmptyPalette && (
                <span className="dc-type-caption absolute text-ink-2 bg-paper-2/90 px-3 py-1.5 rounded-control">
                  {t('creator.embroidery.loadingArtwork')}
                </span>
              )}
            </div>
          </div>

          {/* Mapping */}
          <div className="rounded-card bg-paper p-4 flex flex-col gap-2">
            <div className="flex items-center justify-between gap-3 mb-1">
              <p className="dc-type-label text-mute">
                {t('creator.embroidery.assignThreads')}
              </p>
              <span
                className={cn(
                  'text-xs font-semibold tabular-nums',
                  isOverLimit ? 'text-warning' : 'text-ink'
                )}
              >
                {threadCount} / {maxColors}
              </span>
            </div>

            {mergedCount > 0 && (
              <p className="text-xs text-ink-2 mb-1">
                {t('creator.embroidery.merged', { count: mergedCount })}{' '}
                {t('creator.embroidery.mergedRows')}
              </p>
            )}

            {rows.map((row) => (
              <div
                key={row.hex}
                className="flex items-center gap-2.5 rounded-control bg-paper-2 px-2.5 py-2"
              >
                <ThreadSwatch hex={row.hex} size={24} />
                <span
                  id={`design-color-${row.hex.slice(1)}`}
                  className="flex flex-col w-[68px] flex-none"
                >
                  <span className="dc-type-data tabular-nums text-ink">
                    {row.hex.toUpperCase()}
                  </span>
                  <span className="text-[10px] text-mute tabular-nums">
                    {Math.round(row.share * 100)} %
                    {row.wasAutoMerged && (
                      <span className="ml-1 text-warning">
                        {t('creator.embroidery.mergedTag')}
                      </span>
                    )}
                  </span>
                </span>
                <ThreadSelect
                  value={row.threadId}
                  onChange={(threadId) => assignThread(row.hex, threadId)}
                  isOpen={openRow === row.hex}
                  onOpenChange={(open) => setOpenRow(open ? row.hex : null)}
                  labelledBy={`design-color-${row.hex.slice(1)}`}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3 pt-5 mt-5 border-t border-line">
          <p
            className={cn(
              'flex-1 text-sm',
              isOverLimit ? 'text-warning' : 'text-mute'
            )}
          >
            {isEmptyPalette
              ? t('creator.embroidery.nothingToMap')
              : isOverLimit
                ? t('creator.embroidery.overLimit', {
                    count: threadCount,
                    max: maxColors,
                  })
                : t('creator.embroidery.withinLimit', {
                    count: threadCount,
                    max: maxColors,
                  })}
          </p>

          <button
            type="button"
            onClick={onRemoveMotif}
            disabled={isBusy}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-mute rounded-control hover:bg-paper-2 hover:text-ink-2 transition-colors disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" />
            {t('creator.embroidery.removeArtwork')}
          </button>
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={onCancel}
            disabled={isBusy}
            className="px-4 py-2 text-sm font-medium text-ink-2 bg-paper-2 border border-line rounded-control hover:bg-paper transition-colors disabled:opacity-50"
          >
            {t('common.cancel')}
          </button>
          <button
            ref={confirmButtonRef}
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="px-4 py-2 text-sm font-medium text-primary-fg bg-primary rounded-control hover:brightness-[0.92] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isBusy
              ? t('creator.embroidery.applying')
              : t('creator.embroidery.apply')}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
